import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import { IconX } from "../components/icons.jsx";
import { timeMs } from "../lib/time.js";

function fmtBytes(b) {
  if (!b) return "0";
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
  return (b / (1024 * 1024)).toFixed(1) + " MB";
}

function fmtDuration(start, end) {
  const e = end ? timeMs(end) : Date.now();
  const s = timeMs(start);
  const sec = Math.floor((e - s) / 1000);
  const m = Math.floor(sec / 60);
  const ss = sec % 60;
  return `${m}:${String(ss).padStart(2, "0")}`;
}

function statusOf(s) {
  if (s.ended_at) return { label: "ended", className: "" };
  if (timeMs(s.expires_at) < Date.now() && !s.consumed_at) return { label: "expired", className: "" };
  if (s.consumed_at) return { label: "active", className: "ok" };
  return { label: "pending", className: "warn" };
}

export default function AdminBrowserSessionsPage() {
  const { t } = useT();
  const toast = useToast();
  const [sessions, setSessions] = useState(null);
  const [message, setMessage] = useState(null);

  async function load() {
    try {
      const r = await api("/browser/admin/sessions");
      setSessions(r.sessions);
    } catch (err) {
      toast.error(err.message);
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  async function revoke(id) {
    if (!confirm("End this browser session immediately?")) return;
    try {
      await api(`/browser/admin/sessions/${id}/revoke`, { method: "POST" });
      setMessage({ ok: "Session revoked." });
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  if (!sessions) return <div className="loading">{t("common_loading")}</div>;

  const active = sessions.filter((s) => !s.ended_at && s.consumed_at);

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Administration</div>
          <h1 className="h1">Browser sessions</h1>
        </div>
        <div className="ph-meta">
          <div><strong>{active.length}</strong> active</div>
          <div className="mono" style={{ fontSize: 11, color: "var(--text-dim)" }}>{sessions.length} total · 5s refresh</div>
        </div>
      </div>

      {message?.ok && <div className="chip ok" style={{ marginBottom: 18, padding: "10px 14px", fontSize: 12 }}>{message.ok}</div>}
      {message?.error && <div className="error-line" style={{ marginBottom: 18 }}>{message.error}</div>}

      <div className="section">
        <div className="section-body" style={{ padding: 0 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>User</th>
                <th>Device</th>
                <th>From IP</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Traffic</th>
                <th>Started</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const st = statusOf(s);
                return (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontSize: 13 }}>{s.user_name}</div>
                      <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)" }}>{s.user_email}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{s.device_name}</div>
                      <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)" }}>{s.device_uuid?.slice(0, 12)}…</div>
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>{s.pinned_ip}</td>
                    <td><span className={"chip " + st.className}>{st.label}</span></td>
                    <td className="mono" style={{ fontSize: 11 }}>{fmtDuration(s.created_at, s.ended_at)}</td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      ↓{fmtBytes(s.bytes_in)}<br />
                      ↑{fmtBytes(s.bytes_out)}
                    </td>
                    <td className="mono" style={{ fontSize: 11, color: "var(--text-dim)" }}>
                      {new Date(s.created_at).toISOString().slice(0, 19).replace("T", " ")}
                    </td>
                    <td>
                      {!s.ended_at && (
                        <button className="btn ghost danger" onClick={() => revoke(s.id)}>
                          <IconX size={12} /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
